<section class="dashboard">

  <!-- Mode toggle -->
  <div class="mode-toggle" [class.mode-toggle--single]="!authService.isAuthenticated()">
    <button
      #tabBtn
      class="mode-toggle__tab"
      [class.mode-toggle__tab--active]="mode === 'search'"
      (click)="switchMode('search')">
      Search
    </button>
    <button
      #tabBtn
      class="mode-toggle__tab"
      *ngIf="authService.isAuthenticated()"
      [class.mode-toggle__tab--active]="mode === 'subscribe'"
      (click)="switchMode('subscribe')">
      Subscribe
    </button>
    <span
      class="mode-toggle__indicator"
      [style.left.px]="indicatorLeft"
      [style.width.px]="indicatorWidth">
    </span>
  </div>

  <!-- Search mode -->
  <div class="mode-content" *ngIf="mode === 'search'">
    <p class="mode-hint">Look up tracking data by container or booking number</p>
    <div class="input-group">
      <input
        [(ngModel)]="searchString"
        type="text"
        class="form-control form-control-lg"
        [class.dimmed-border]="!searchString.trim().length"
        (keyup.enter)="search()"
        placeholder="Enter a container or booking number">

      <button
        class="btn btn-lg btn-primary px-md-4 pt-2"
        (click)="search()"
        type="button"
        [disabled]="!searchString.trim().length">
        <svg class="me-sm-2" fill="currentColor" width="20" height="20" style="position: relative; top: -2px;">
          <use href="/assets/icons.svg#icon-search"></use>
        </svg>
        <span class="d-none d-sm-inline">Search</span>
      </button>
    </div>
  </div>

  <!-- Subscribe mode -->
  <div class="mode-content" *ngIf="mode === 'subscribe'">
    <p class="mode-hint">Start tracking a new shipment</p>

    <div class="subscribe-form">
      <div class="field">
        <label class="field-label">Carrier</label>
        <select class="form-select" [(ngModel)]="selectedScac">
          <option value="">Select carrier</option>
          <option *ngFor="let carrier of carriers" [value]="carrier.code">
            {{ carrier.code }} — {{ carrier.name }}
          </option>
        </select>
      </div>

      <div class="field" *ngIf="selectedScac">
        <label class="field-label">Tracking Reference</label>
        <div class="field-combo">
          <select class="form-select ref-type-select" [(ngModel)]="selectedRefType" (ngModelChange)="refTouched = false">
            <option *ngFor="let t of availableRefTypes" [value]="t.key">{{ t.label }}</option>
          </select>
          <input
            [(ngModel)]="refValue"
            type="text"
            class="form-control"
            [class.is-invalid]="showContainerError"
            [attr.maxlength]="refMaxLength"
            (ngModelChange)="refTouched = false"
            (blur)="refTouched = true"
            (keyup.enter)="subscribe()"
            placeholder="Enter reference">
        </div>
        <small class="field-error" *ngIf="showContainerError">
          Not a valid container number — check the digits (ISO 6346 check digit failed).
        </small>
      </div>

      <div class="field" *ngIf="selectedScac">
        <label class="field-label">Subscription reference <span class="field-optional">optional</span></label>
        <input
          [(ngModel)]="subRefValue"
          type="text"
          class="form-control"
          (keyup.enter)="subscribe()"
          placeholder="Auto-generated if left blank">
      </div>

      <div class="subscribe-footer" *ngIf="selectedScac">
        <button
          class="btn btn-primary w-100"
          (click)="subscribe()"
          type="button"
          [disabled]="!hasSubscribeRef() || !selectedScac || loading || containerNumberInvalid">
          <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
          {{ loading ? '' : 'Subscribe' }}
        </button>
      </div>
    </div>
  </div>

  <div class="error-bar" *ngIf="errorMessage">
    {{ errorMessage }}
  </div>

  <div class="success-bar" *ngIf="successMessage">
    {{ successMessage }}
  </div>

</section>


<!-- Subscriptions (only when authenticated, hidden on the subscribe tab) -->
<section class="subscriptions" *ngIf="authService.isAuthenticated() && mode === 'search'">
  <div class="subs-header">
    <h4 class="subs-title">Active Subscriptions</h4>

    <div class="subs-search" [class.subs-search--filled]="subSearch.length">
      <svg class="subs-search__icon" fill="currentColor" width="14" height="14">
        <use href="/assets/icons.svg#icon-search"></use>
      </svg>
      <input
        class="subs-search__input"
        type="text"
        [(ngModel)]="subSearch"
        (ngModelChange)="onSearchChange($event)"
        placeholder="Search reference"
        autocomplete="off"
        spellcheck="false"
        aria-label="Search by subscription reference">
      <button
        *ngIf="subSearch"
        class="subs-search__clear"
        type="button"
        (click)="clearSearch()"
        aria-label="Clear search">×</button>
    </div>
  </div>

  <div *ngIf="loadingSubscriptions" class="loading-state">
    <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
  </div>

  <p class="empty-state" *ngIf="!loadingSubscriptions && subscriptions.length === 0">
    {{ searchActive ? 'No subscriptions match “' + subSearch.trim() + '”' : 'No active subscriptions' }}
  </p>

  <div class="sub-table" *ngIf="!loadingSubscriptions && subscriptions.length > 0">
    <table class="table mb-0">
      <thead>
        <tr>
          <th>Carrier</th>
          <th>Tracking Reference</th>
          <th>Subscription Reference</th>
          <th>Status</th>
          <th>Created</th>
          <th>Expires</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let sub of pagedSubscriptions" class="sub-row">
          <td class="carrier-cell">{{ sub.scacCode }}</td>
          <td class="ref-cell">
            <span class="ref-chip" *ngIf="refTypeCode(sub)" [title]="refTypeLabel(sub)">{{ refTypeCode(sub) }}</span>
            <span class="ref-value">{{ sub.equipmentNumber || sub.bookingNumber || sub.billNumber }}</span>
          </td>
          <td class="sub-ref-cell">{{ subRef(sub) }}</td>
          <td class="connection-cell" [title]="connectionTooltip(sub)">
            <span class="status-dot" [ngClass]="'status-dot--' + connectionState(sub)"></span>
            {{ connectionLabel(sub) }}
          </td>
          <td class="text-muted">{{ sub.subscriptionCreationDate ? (sub.subscriptionCreationDate | date:'mediumDate') : '—' }}</td>
          <td class="text-muted">{{ sub.subscriptionExpiryDate | date:'mediumDate' }}</td>
          <td class="text-end">
            <div class="row-actions">
              <button class="track-link" (click)="trackSubscription(sub)">
                View
                <svg fill="currentColor" width="14" height="14" style="position: relative; top: -1px;">
                  <use href="/assets/icons.svg#icon-right"></use>
                </svg>
              </button>
              <button class="delete-link" *ngIf="!isClosed(sub)" (click)="promptDelete(sub)" aria-label="Delete subscription">
                Delete
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" *ngIf="totalPages > 1">
      <button
        class="page-btn"
        [disabled]="page === 1"
        (click)="page = page - 1">
        <svg fill="currentColor" width="12" height="12">
          <use href="/assets/icons.svg#icon-left"></use>
        </svg>
      </button>
      <span class="page-info">{{ page }} of {{ totalPages }}</span>
      <button
        class="page-btn"
        [disabled]="page === totalPages"
        (click)="page = page + 1">
        <svg fill="currentColor" width="12" height="12">
          <use href="/assets/icons.svg#icon-right"></use>
        </svg>
      </button>
    </div>
  </div>

  <div class="confirm-overlay" *ngIf="confirmDeleteSub" (click)="cancelDelete()">
    <div class="confirm-modal" (click)="$event.stopPropagation()">
      <h4>Delete subscription?</h4>
      <p class="confirm-text">
        Tracking for
        <strong>{{ confirmDeleteSub.equipmentNumber || confirmDeleteSub.bookingNumber || confirmDeleteSub.billNumber }}</strong>
        ({{ confirmDeleteSub.scacCode }}) will stop immediately. This can’t be undone, and the subscription reference can’t be reused.
      </p>
      <div class="confirm-error" *ngIf="deleteError">{{ deleteError }}</div>
      <div class="confirm-actions">
        <button class="btn-cancel" [disabled]="deleting" (click)="cancelDelete()">Cancel</button>
        <button class="btn-delete" [disabled]="deleting" (click)="confirmDelete()">
          <span *ngIf="deleting" class="spinner-border spinner-border-sm me-1"></span>
          Delete
        </button>
      </div>
    </div>
  </div>
</section>
